// Portfolio Insights — pure helpers voor status-afleiding, churn, en KPI's.
//
// Werkt op de bestaande order-vorm (zoals mapApiOrder produceert). Geen
// backend-aanroepen, geen state — pure functies zodat ze testbaar zijn.
// De inputs zijn al scope-gefilterd door de backend (/api/orders).

export const ACTIVE_CP_STATUSES = new Set(['owned', 'in', 'int_in']);
export const UPCOMING_WINDOW_DAYS = 90;
// Statussen die als "vertrokken" tellen in kengetallen, churn en de maandreeks:
// definitief weg (lost), datum voorbij maar markt bevestigde nog niet (leaving)
// en de overstap naar een ander eigen energielabel (internal_out — voor het
// label een vertrek; op groepsniveau valt dat weg tegen de int_in-aanwas elders).
export const LOST_STATUSES = new Set(['lost', 'leaving', 'internal_out']);

// ───────────────────────────────────────────────────────────────────────────
// VERTREKMODEL — gebaseerd op de MIG6-scenario's zoals de bron ze levert.
//
// De workflow (`flowIdentifier`) bepaalt WAT er gebeurde; bij een Stop Contract
// verfijnt `stopContractType` het scenario. Gemeten over 20.822 orders komen
// uitsluitend deze waarden voor:
//
//   flowIdentifier            Start Contract 6.667 · Loss Flow 4.116 ·
//                             Manage Contract 2.135 · Stop Contract 676 ·
//                             Mystery Switch Received 189 ·
//                             Initiate Leaving Customer 145 ·
//                             Mystery Switch Self-Detected 129 · Rectification 53
//   stopContractType          ResidentialDrop 359 · NonResidentialDrop 254 ·
//                             ResidentialEOC 53 · NonResidentialEOC 10  (= 676)
//
// KERNREGEL: er is ALLEEN sprake van een drop bij een Stop Contract met
// ResidentialDrop of NonResidentialDrop. Een Stop Contract met ResidentialEOC of
// NonResidentialEOC is een einde-contract, GEEN drop. Vroeger werd dat door
// datum-heuristiek en keyword-matching door elkaar gehaald, waardoor vertrekken
// ten onrechte als drop verschenen.
// ───────────────────────────────────────────────────────────────────────────

export const FLOW_START_CONTRACT = 'start contract';
export const FLOW_MANAGE_CONTRACT = 'manage contract';
export const FLOW_STOP_CONTRACT = 'stop contract';
export const FLOW_LOSS = 'loss flow';
export const FLOW_ILC = 'initiate leaving customer';
export const FLOW_RECTIFICATION = 'rectification';

// Soorten vertrek. `none` = de workflow is geen vertrek (contractstart, wijziging,
// rectificatie); `unknown` = wel een exit-datum maar geen bruikbare workflow.
export const DEPARTURE_DROP = 'drop';
export const DEPARTURE_EOC = 'eoc';
export const DEPARTURE_LOSS = 'loss';
export const DEPARTURE_ILC = 'ilc';
export const DEPARTURE_MYSTERY_SWITCH = 'mystery_switch';
export const DEPARTURE_INTERNAL = 'internal';     // interne overstap naar een eigen energielabel (CP int_out)
export const DEPARTURE_NONE = 'none';
export const DEPARTURE_UNKNOWN = 'unknown';

export const DEPARTURE_KIND_LABELS = {
  [DEPARTURE_DROP]: 'Drop',
  [DEPARTURE_EOC]: 'Einde contract (EOC)',
  [DEPARTURE_LOSS]: 'Loss — andere leverancier',
  [DEPARTURE_ILC]: 'Verhuis (ILC)',
  [DEPARTURE_MYSTERY_SWITCH]: 'Mystery switch',
  [DEPARTURE_INTERNAL]: 'Interne overstap',
  [DEPARTURE_NONE]: 'Geen vertrek',
  [DEPARTURE_UNKNOWN]: 'Onbekend',
};

// ───────────────────────────────────────────────────────────────────────────
// VERTREKSTAND — de stand van een aansluiting, uit workflow + stap + CP-status.
//
// Dit is de port van `backend/src/util/vertrek.js` (vertrekStand). De server
// stuurt de uitkomst mee als `vertrekStand`, `vertreksoort`, `vertrekDatum`,
// `vertrekUitkomst`, `vertrekHistoriek*` en `vertrekTeruggewonnen`; de
// berekening hieronder is de TERUGVAL voor gecachte data van vóór die velden.
// Zie de toelichting in de backend-module voor het model; kort:
//
//   1. leidende workflow = current (als die over start/vertrek gaat), anders last;
//      Manage Contract en Rectification zijn nooit leidend;
//   2. uitkomst uit de stap: afgerond / geannuleerd / lopend / betwist;
//   3. CP-status is de scheidsrechter: ext = weg, owned/in/int_in = niet weg,
//      draft = nooit gestart, int_out = interne overstap;
//   4. de andere workflow (of een geannuleerde) blijft als historiek zichtbaar.
//
// Wijzig je hier iets, wijzig het ook in de backend: `src/portfolio.test.js` en
// `backend/test/vertrek.test.js` draaien op dezelfde gevallen.
// ───────────────────────────────────────────────────────────────────────────

export const STAND_ACTIEF = 'actief';
export const STAND_ZAL_VERTREKKEN = 'zal_vertrekken';
export const STAND_GEPLAND_VERTREK = 'gepland_vertrek';
export const STAND_IN_AFHANDELING = 'vertrek_in_afhandeling';
export const STAND_VERTROKKEN = 'vertrokken';
export const STAND_INTERNE_OVERSTAP = 'interne_overstap';
export const STAND_NOOIT_GESTART = 'nooit_gestart';
export const STAND_TE_CONTROLEREN = 'te_controleren';
export const STAND_ONBEKEND = 'onbekend';

export const UITKOMST_AFGEROND = 'afgerond';
export const UITKOMST_GEANNULEERD = 'geannuleerd';
export const UITKOMST_LOPEND = 'lopend';
export const UITKOMST_BETWIST = 'betwist';

// Portfolio-status (de `_status` op elk record) per vertrekstand. De vier
// oorspronkelijke waarden (active / will_leave / lost / unknown) blijven bestaan;
// daar komen leaving (datum voorbij, markt bevestigde nog niet), internal_out,
// never_started en review bij. Een gepland vertrek ver in de toekomst blijft
// `active` — de aansluiting levert nog — met `_planned` als markering.
export const STAND_NAAR_STATUS = {
  [STAND_ACTIEF]: 'active',
  [STAND_ZAL_VERTREKKEN]: 'will_leave',
  [STAND_GEPLAND_VERTREK]: 'active',
  [STAND_IN_AFHANDELING]: 'leaving',
  [STAND_VERTROKKEN]: 'lost',
  [STAND_INTERNE_OVERSTAP]: 'internal_out',
  [STAND_NOOIT_GESTART]: 'never_started',
  [STAND_TE_CONTROLEREN]: 'review',
  [STAND_ONBEKEND]: 'unknown',
};

export const PORTFOLIO_STATUS_LABEL = {
  active: 'Actief',
  will_leave: 'Zal vertrekken',
  leaving: 'Vertrek in afhandeling',
  lost: 'Vertrokken',
  internal_out: 'Interne overstap',
  never_started: 'Nooit gestart',
  review: 'Te controleren',
  unknown: 'Onbekend',
};

const isoDag = (d) => (d ? d.toISOString().substring(0, 10) : '');

// Vertreksoort van één workflow (flow + stop-contract-type); null = geen vertrek.
export function soortVanWorkflow(flow, stopType) {
  const f = normLower(flow);
  // Zonder flow maar mét stop-contract-type: dat type komt in de bron enkel bij
  // een Stop Contract voor. Lees het dan als zodanig (oude cache, tests).
  if (!f && normLower(stopType)) return soortVanWorkflow(FLOW_STOP_CONTRACT, stopType);
  if (!f) return null;
  if (f === FLOW_STOP_CONTRACT) {
    const t = normLower(stopType);
    if (t.includes('drop')) return DEPARTURE_DROP;
    if (t.includes('eoc')) return DEPARTURE_EOC;
    return DEPARTURE_UNKNOWN;
  }
  if (f === FLOW_LOSS) return DEPARTURE_LOSS;
  if (f === FLOW_ILC) return DEPARTURE_ILC;
  if (f.startsWith('mystery switch')) return DEPARTURE_MYSTERY_SWITCH;
  return null;
}

// Uitkomst van een workflow uit de stap-benaming ("Stopcontract Step Cancelled",
// "Losscontract Step Lossended", …). Regex op fragmenten: de bron schrijft
// "Startconttract" met een dubbele t en varieert in hoofdletters.
export function uitkomstVanStap(step) {
  const s = normLower(step);
  if (!s) return '';
  if (/cancel|failed|rejected/.test(s)) return UITKOMST_GEANNULEERD;
  if (/dispute/.test(s)) return UITKOMST_BETWIST;
  if (/external|lossended|losscompleted|supplyended|finished|succeeded/.test(s)) return UITKOMST_AFGEROND;
  return UITKOMST_LOPEND;
}

const isLeidbaar = (flow) => {
  const f = normLower(flow);
  return !!f && f !== FLOW_MANAGE_CONTRACT && f !== FLOW_RECTIFICATION;
};

function workflowsVan(rec) {
  const r = rec || {};
  const heeftGesplitst = 'workflowCurrentFlow' in r || 'workflowLastFlow' in r;
  const current = { flow: r.workflowCurrentFlow || '', step: r.workflowCurrentStep || '', lossDate: r.workflowCurrentLossDate || '', stopType: r.workflowCurrentStopType || '', stopReason: r.workflowCurrentStopReason || '' };
  const last = heeftGesplitst
    ? { flow: r.workflowLastFlow || '', step: r.workflowLastStep || '', lossDate: r.workflowLastLossDate || '', stopType: r.workflowLastStopType || '', stopReason: r.workflowLastStopReason || '' }
    : { flow: r.flowIdentifier || '', step: '', lossDate: r.lossDate || '', stopType: r.stopContractType || '', stopReason: r.stopContractTypeDescription || '' };
  return { current, last };
}

// Korte soortnamen voor de historiek-tekst; zie backend/src/util/vertrek.js.
const HISTORIEK_NAMEN = {
  [DEPARTURE_DROP]: 'Drop', [DEPARTURE_EOC]: 'Einde contract', [DEPARTURE_LOSS]: 'Loss',
  [DEPARTURE_ILC]: 'Verhuis', [DEPARTURE_MYSTERY_SWITCH]: 'Mystery switch', [DEPARTURE_INTERNAL]: 'Interne overstap',
};
export function historiekLabel(soort, datum, uitkomst) {
  const naam = HISTORIEK_NAMEN[soort] || DEPARTURE_KIND_LABELS[DEPARTURE_UNKNOWN];
  // De datum is ALTIJD de geplande vertrekdatum (loss_date uit de workflow);
  // een annuleringsdatum kent de bron niet. Zeg dat dus ook zo: "Drop
  // geannuleerd op 17/10/2026" las als een annulering op die dag (en in de
  // toekomst zelfs als een nog te annuleren drop).
  const dag = datum ? String(datum).split('-').reverse().join('/') : '';
  const wanneer = dag ? ` op ${dag}` : '';
  if (uitkomst === UITKOMST_GEANNULEERD) return `${naam} geannuleerd${dag ? ` (was gepland op ${dag})` : ''}`;
  if (uitkomst === UITKOMST_AFGEROND) return `Teruggewonnen na ${naam.toLowerCase()}${wanneer}`;
  if (uitkomst === UITKOMST_BETWIST) return `${naam} betwist${dag ? ` (gepland op ${dag})` : ''}`;
  return `${naam}${wanneer}`;
}

// Lokale berekening (terugval). Levert { stand, soort, uitkomst, datum, stopType,
// stopReason, flow, historiek, teruggewonnen }.
export function berekenVertrekStand(rec, now = new Date()) {
  const r = rec || {};
  const cp = normLower(r.connectionPointStatus);
  const { current, last } = workflowsVan(r);
  const leidend = isLeidbaar(current.flow) ? current : last;
  const leidendIsCurrent = leidend === current;
  const flow = normLower(leidend.flow);
  // Een stopdatum zonder enige workflow (oude cache): een aangekondigd vertrek van
  // onbekende soort. In de bron komt loss_date enkel bij Loss/Stop voor.
  const soortLeidend = soortVanWorkflow(leidend.flow, leidend.stopType)
    || (!normLower(leidend.flow) && parseDate(leidend.lossDate) ? DEPARTURE_UNKNOWN : null);
  const uitkomst = uitkomstVanStap(leidend.step);
  const soortLast = soortVanWorkflow(last.flow, last.stopType);
  const uitkomstLast = uitkomstVanStap(last.step);
  const effEnd = parseDate(r.effectiveEndDate);
  const effStart = parseDate(r.effectiveStartDate);
  const vertrekDatumLeidend = soortLeidend && uitkomst !== UITKOMST_GEANNULEERD ? (parseDate(leidend.lossDate) || effEnd) : null;

  let historiek = null;
  if (soortLeidend && uitkomst === UITKOMST_GEANNULEERD) {
    historiek = { soort: soortLeidend, datum: isoDag(parseDate(leidend.lossDate)), uitkomst: UITKOMST_GEANNULEERD };
  } else if (leidendIsCurrent && soortLast && last.flow) {
    historiek = { soort: soortLast, datum: isoDag(parseDate(last.lossDate)), uitkomst: uitkomstLast };
  }
  if (historiek) historiek.label = historiekLabel(historiek.soort, historiek.datum, historiek.uitkomst);
  const nieuweStartNaVertrek = leidendIsCurrent && flow === FLOW_START_CONTRACT && !!soortLast && uitkomstLast !== UITKOMST_GEANNULEERD;

  const basis = { soort: DEPARTURE_NONE, uitkomst, datum: '', stopType: leidend.stopType || '', stopReason: leidend.stopReason || '', flow: leidend.flow || '', historiek, teruggewonnen: false };
  const klaar = (stand, extra = {}) => ({ stand, ...basis, ...extra });

  if (cp === 'draft') return klaar(STAND_NOOIT_GESTART);
  if (cp === 'ext' || cp === 'external') {
    const exit = vertrekDatumLeidend || effEnd;
    if (!effStart && !exit) return klaar(STAND_NOOIT_GESTART, { soort: soortLeidend || DEPARTURE_NONE });
    const soort = (soortLeidend && uitkomst !== UITKOMST_GEANNULEERD) ? soortLeidend
      : (soortLast && uitkomstLast !== UITKOMST_GEANNULEERD) ? soortLast
        : DEPARTURE_UNKNOWN;
    return klaar(STAND_VERTROKKEN, { soort, datum: isoDag(exit) });
  }
  if (cp === 'int_out') return klaar(STAND_INTERNE_OVERSTAP, { soort: DEPARTURE_INTERNAL, datum: isoDag(vertrekDatumLeidend || effEnd) });
  if (cp === 'owned' || cp === 'in' || cp === 'int_in') {
    if (soortLeidend) {
      if (uitkomst === UITKOMST_GEANNULEERD) return klaar(STAND_ACTIEF);
      if (uitkomst === UITKOMST_BETWIST || uitkomst === UITKOMST_AFGEROND) return klaar(STAND_TE_CONTROLEREN, { soort: soortLeidend, datum: isoDag(vertrekDatumLeidend) });
      if (!vertrekDatumLeidend) return klaar(STAND_ZAL_VERTREKKEN, { soort: soortLeidend });
      if (vertrekDatumLeidend <= now) return klaar(STAND_IN_AFHANDELING, { soort: soortLeidend, datum: isoDag(vertrekDatumLeidend) });
      if (vertrekDatumLeidend <= addDays(now, UPCOMING_WINDOW_DAYS)) return klaar(STAND_ZAL_VERTREKKEN, { soort: soortLeidend, datum: isoDag(vertrekDatumLeidend) });
      return klaar(STAND_GEPLAND_VERTREK, { soort: soortLeidend, datum: isoDag(vertrekDatumLeidend) });
    }
    return klaar(STAND_ACTIEF, { teruggewonnen: nieuweStartNaVertrek });
  }
  const soortZonderCp = (soortLeidend && uitkomst !== UITKOMST_GEANNULEERD) ? soortLeidend
    : (!soortLeidend && (effEnd || parseDate(leidend.lossDate))) ? DEPARTURE_UNKNOWN
      : DEPARTURE_NONE;
  const datumZonderCp = vertrekDatumLeidend || (soortZonderCp === DEPARTURE_UNKNOWN ? (parseDate(leidend.lossDate) || effEnd) : null);
  return klaar(STAND_ONBEKEND, { soort: soortZonderCp, datum: isoDag(datumZonderCp) });
}

// De vertrekstand van een record: het serverveld als dat er is, anders de
// lokale berekening. Beide leveren dezelfde vorm.
export function vertrekStand(rec, now = new Date()) {
  if (rec && rec.vertrekStand) {
    const historiek = rec.vertrekHistoriek
      ? { soort: rec.vertrekHistoriekSoort || DEPARTURE_UNKNOWN, datum: rec.vertrekHistoriekDatum || '', uitkomst: rec.vertrekHistoriekUitkomst || '', label: rec.vertrekHistoriek }
      : null;
    return {
      stand: rec.vertrekStand,
      soort: rec.vertreksoort || DEPARTURE_NONE,
      uitkomst: rec.vertrekUitkomst || '',
      datum: rec.vertrekDatum || '',
      stopType: rec.stopContractType || '',
      stopReason: rec.stopContractTypeDescription || '',
      flow: rec.flowIdentifier || '',
      historiek,
      teruggewonnen: !!rec.vertrekTeruggewonnen,
    };
  }
  return berekenVertrekStand(rec, now);
}

// Stop-contract-scenario's, met hun MIG6-betekenis.
export const STOP_CONTRACT_TYPE_LABELS = {
  residentialdrop: 'Residential Drop',
  nonresidentialdrop: 'Non-residential Drop',
  residentialeoc: 'Residential EOC',
  nonresidentialeoc: 'Non-residential EOC',
};

// Leesbare omschrijving per workflow. De snake_case-sleutels onderaan zijn
// legacy: die vorm komt in de huidige bron niet meer voor, maar een omgeving die
// hem nog levert blijft zo leesbaar.
export const FLOW_IDENTIFIER_MAP = {
  'start contract': 'Start contract',
  'manage contract': 'Contractwijziging',
  'stop contract': 'Stop contract',
  'loss flow': 'Loss — andere leverancier nam over',
  'initiate leaving customer': 'Verhuis (ILC)',
  'mystery switch received': 'Mystery switch (ontvangen)',
  'mystery switch self-detected': 'Mystery switch (zelf vastgesteld)',
  rectification: 'Rectificatie',
  customer_initiated_switch: 'Klant wisselde naar andere leverancier',
  customer_initiated_termination: 'Klant beëindigde contract',
  non_payment: 'Wanbetaling',
  move_out: 'Verhuizing',
  contract_expired: 'Contract afgelopen',
  bankruptcy: 'Faillissement',
  death: 'Overlijden',
  fraud: 'Fraude',
};

const normLower = v => String(v || '').trim().toLowerCase();

export function parseDate(v) {
  if (!v) return null;
  // Accept ISO date or datetime
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function ymKey(d) {
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

// Bepaal de "exit-datum" van een record: de eerste datum waarop de
// aansluiting de leverancier verlaat (of zou verlaten).
//
// Sinds het vertrekmodel van 10-09-2026 is dit de datum uit de LEIDENDE
// workflow (de loss-/dropdatum, de marktdatum), met de effectieve einddatum als
// terugval. Een geannuleerd vertrek heeft géén exit-datum meer: die datum is
// historiek, geen vertrek.
export function exitDate(rec, now = new Date()) {
  if (!rec) return null;
  const s = vertrekStand(rec, now);
  return s.datum ? parseDate(s.datum) : null;
}

// Portfolio-status van een record (zie STAND_NAAR_STATUS). De CP-status is de
// scheidsrechter, niet de datum: een gepasseerde stopdatum op een aansluiting die
// nog owned is, is "vertrek in afhandeling" (leaving) en geen vertrek.
export function derivePortfolioStatus(rec, now = new Date()) {
  if (!rec) return 'unknown';
  return STAND_NAAR_STATUS[vertrekStand(rec, now).stand] || 'unknown';
}

// Vroegtijdig vertrek: effective end is vroeger dan delivery end.
// Defensief: alleen markeren als beide datums geldig zijn.
export function isEarlyLeaver(rec) {
  const effEnd = parseDate(rec.effectiveEndDate);
  const delEnd = parseDate(rec.deliveryEndDate);
  if (!effEnd || !delEnd) return false;
  return effEnd < delEnd;
}

// Eén representatief order per EAN GSRN — het order dat de HUIDIGE stand van de
// aansluiting draagt. Records zonder GSRN worden uitgesloten.
//
// Keuzeregel (sinds 10-09-2026): eerst op "rang", dan op leveringsstart (laatste
// wint), dan op orderdatum/-tijd (laatste wint).
//   rang 0  geleverd of lopend order
//   rang 1  ext-order dat nooit geleverd heeft (bv. een geannuleerde verlenging)
//   rang 2  draft
// Vroeger won gewoon de jongste orderdatum. Dat zette bij 354 EAN's een draft
// (pijplijn) vóór het lopende contract en bij 29 owned-EAN's een verlopen order
// vóór de verlenging die er wél nog loopt — de verkeerde rij vertegenwoordigde
// de aansluiting. Met deze regel blijft een draft alleen over als er niets
// anders is (dan is "nooit gestart" ook de juiste stand).
export function dedupeByGSRNLatest(records) {
  const byGSRN = new Map();
  const ts = r => r.orderDateTime || r.orderDate || '';
  const start = r => String(r.deliveryStartDate || r.effectiveStartDate || '').substring(0, 10);
  const rang = r => {
    const cp = normLower(r.connectionPointStatus);
    if (cp === 'draft') return 2;
    if ((cp === 'ext' || cp === 'external') && !parseDate(r.effectiveStartDate) && !parseDate(r.effectiveEndDate)) return 1;
    return 0;
  };
  const beter = (a, b) => {           // is a een betere representant dan b?
    const ra = rang(a), rb = rang(b);
    if (ra !== rb) return ra < rb;
    const sa = start(a), sb = start(b);
    if (sa !== sb) return sa > sb;
    return ts(a) > ts(b);
  };
  for (const r of records || []) {
    const g = r.connectionPointGSRN;
    if (!g) continue;
    const cur = byGSRN.get(g);
    if (!cur || beter(r, cur)) byGSRN.set(g, r);
  }
  return [...byGSRN.values()];
}

// Klassificatie van een vertrek, uitsluitend op de workflow (+ stop-contract-type).
// GEEN keyword-matching en GEEN datum-heuristiek meer: die maakten van elk
// vroegtijdig einde een "drop", ook als het gewoon een einde-contract, een loss
// of een verhuis was.
//
//   Stop Contract + …Drop            → drop            (wanbetaling; levering stopgezet)
//   Stop Contract + …EOC             → eoc             (formeel overeengekomen einde)
//   Loss Flow                        → loss            (andere leverancier neemt over)
//   Initiate Leaving Customer        → ilc             (verhuisscenario)
//   Mystery Switch (beide varianten) → mystery_switch
//   Start/Manage Contract, Rectification → none        (geen vertrek)
//   verder                           → unknown bij een exit-datum, anders none
//
// De SERVER rekent dit tegenwoordig zelf uit en stuurt het mee als `vertreksoort`
// (backend/src/util/vertrek.js). Dat moest, want de ruwe workflow-velden gaan niet
// meer naar partnerrollen — zie de toelichting daar. Staat het veld er, dan is dat
// de bron; de berekening hieronder blijft als terugval voor gecachte data van vóór
// die wijziging.
export function classifyDeparture(rec) {
  if (!rec) return DEPARTURE_UNKNOWN;
  return vertrekStand(rec).soort || DEPARTURE_NONE;
}

// Residentieel of niet-residentieel, afgeleid uit het stop-contract-type van de
// leidende workflow. Alleen gevuld bij een Stop Contract; bij de andere
// workflows levert de bron dit niet. Komt bij voorkeur van de server
// (`vertrekSegment`).
export function departureSegment(rec) {
  if (rec && rec.vertrekSegment) return rec.vertrekSegment;
  const t = normLower(rec && vertrekStand(rec).stopType);
  if (!t) return null;
  if (t.startsWith('nonresidential') || t.startsWith('non-residential')) return 'non_residential';
  if (t.startsWith('residential')) return 'residential';
  return null;
}

// Records zonder vertrek (actief, geannuleerd vertrek, nooit gestart) blijven
// buiten elke vertrek-breakdown.
export function isExcludedDepartureReason(rec) {
  return classifyDeparture(rec) === DEPARTURE_NONE;
}

// Vertrekreden in 1 leesbare string. Prioriteit:
//   1. stopContractType → het MIG6-scenario (Residential Drop, Non-residential EOC, …)
//   2. flowIdentifier → de workflow (Loss, Verhuis (ILC), Mystery switch, …)
//   3. stopContractTypeDescription → ruwe brontekst als laatste vangnet
// Zonder vertrek: de eerdere situatie ("Drop geannuleerd op …") als die er is.
//
// De omschrijving stond vroeger vooraan, maar die is Engelstalig én misleidend:
// "The contract was terminated by us." staat op alle EOC-records en "Customer has
// outstanding debts." op alle drops, waardoor het scenario zelf onzichtbaar werd.
//
// De server stuurt dit mee als `vertrekReden`. Voor admin/ET is dat exact wat de
// regels hieronder opleveren; voor partnerrollen de neutrale soortbenaming, omdat
// de laatste stap hieronder op ruwe, onvertaalde brontekst kan terugvallen.
export function humanizeLossReason(rec) {
  if (rec && rec.vertrekReden) return rec.vertrekReden;
  if (!rec) return 'Onbekend';
  const s = vertrekStand(rec);
  if (s.soort === DEPARTURE_INTERNAL) return DEPARTURE_KIND_LABELS[DEPARTURE_INTERNAL];
  if (s.soort === DEPARTURE_NONE) {
    if (s.historiek) return s.historiek.label;
    const desc = (s.stopReason || '').trim();
    if (desc) return desc;
    // Een flow die we niet kennen (legacy snake_case-waarden) krijgt zijn eigen
    // label; de bekende niet-vertrekflows (start/wijziging/rectificatie) niet.
    const f = normLower(s.flow);
    if (f && f !== FLOW_START_CONTRACT && f !== FLOW_MANAGE_CONTRACT && f !== FLOW_RECTIFICATION) return FLOW_IDENTIFIER_MAP[f] || String(s.flow).trim();
    return DEPARTURE_KIND_LABELS[DEPARTURE_NONE];
  }
  const t = (s.stopType || '').trim();
  if (t) return STOP_CONTRACT_TYPE_LABELS[normLower(t)] || t;
  const flow = (s.flow || '').trim();
  if (flow && s.soort !== DEPARTURE_UNKNOWN) return FLOW_IDENTIFIER_MAP[normLower(flow)] || flow;
  const desc = (s.stopReason || '').trim();
  if (desc) return desc;
  return 'Onbekend';
}

// Actieve duur in maanden (effectief gemeten):
// effectiveStartDate → effectiveEndDate (of vandaag bij nog-actief)
export function activeDurationMonths(rec, now = new Date()) {
  const start = parseDate(rec.effectiveStartDate) || parseDate(rec.deliveryStartDate);
  if (!start) return null;
  const end = parseDate(rec.effectiveEndDate) || now;
  if (end <= start) return 0;
  return ((end.getFullYear() - start.getFullYear()) * 12) + (end.getMonth() - start.getMonth());
}

// Bin een aantal maanden in een histogram-bucket
export function durationBucket(months) {
  if (months == null) return null;
  if (months < 6) return '< 6m';
  if (months < 12) return '6–12m';
  if (months < 24) return '1–2j';
  if (months < 36) return '2–3j';
  if (months < 60) return '3–5j';
  return '> 5j';
}

// ───────────────────────────────────────────────────────────────────────────
// CONTRACTDUUR VAN VERTROKKEN KLANTEN
//
// Eén klant kan meerdere opeenvolgende orders op dezelfde aansluiting hebben
// (contractverlenging, productwissel, correctie). Die vormen samen ÉÉN
// aaneensluitende leveringsperiode. De contractduur loopt dus van de start van
// het eerste order tot de effectieve einddatum van het laatste order.
//
// Zolang die periode nog doorloopt (de aansluiting is nog actief, of er is geen
// effectieve einddatum) is de klant niet weg en telt de periode NIET mee.
// ───────────────────────────────────────────────────────────────────────────

// CP-status van een aansluiting die de portefeuille verlaten heeft. Bewust
// alleen "ext": `draft` is een order dat nooit gelopen heeft en `int_out` een
// interne uitstroom — geen van beide is een vertrokken klant.
export const DEPARTED_CP_STATUSES = new Set(['ext', 'external']);

// Orders die nooit geleverd hebben en dus niet in een leveringsperiode horen.
// Gemeten: van de 6.356 draft-orders heeft er 0 een effectieve einddatum en 1 een
// effectieve startdatum. Ze meenemen zou de keten kunstmatig verlengen en — erger —
// 915 werkelijk vertrokken aansluitingen onzichtbaar maken, omdat het draft-order
// achteraan de reeks komt en de keten dan niet meer op een vertrokken status eindigt.
export const NON_DELIVERING_CP_STATUSES = new Set(['draft']);

const DAY_MS = 24 * 60 * 60 * 1000;

// Start en einde van één order. Start = leveringsstart (effectieve start als
// terugval); einde = de effectieve einddatum, met de geplande einddatum als
// terugval zodat een lopende keten niet voortijdig "open" oogt.
const orderStart = o => parseDate(o.deliveryStartDate) || parseDate(o.effectiveStartDate);
const orderEnd = o => parseDate(o.effectiveEndDate) || parseDate(o.deliveryEndDate);

// Volledig verstreken maanden tussen twee datums. Een periode van 1/1 tot 15/2
// telt als 1 maand, niet als 2 — anders zou elke duur naar boven vertekenen.
export function monthsBetween(start, end) {
  if (!start || !end || end <= start) return 0;
  let m = ((end.getFullYear() - start.getFullYear()) * 12) + (end.getMonth() - start.getMonth());
  if (end.getDate() < start.getDate()) m -= 1;
  return Math.max(0, m);
}

// Groepeert de orders van één GSRN in aaneensluitende leveringsperiodes.
// Een order hoort bij de lopende keten als het start vóór (of op) het einde van
// wat er al ligt; een dag speling vangt het geval "oud eindigt 31/12, nieuw
// start 1/1" op, dat in de praktijk één doorlopende levering is.
function chainOrders(list) {
  const met = list
    .filter(o => orderStart(o) && !NON_DELIVERING_CP_STATUSES.has(normLower(o.connectionPointStatus)))
    .sort((a, b) => orderStart(a) - orderStart(b));
  const ketens = [];
  for (const o of met) {
    const huidig = ketens[ketens.length - 1];
    const s = orderStart(o);
    if (huidig && s - huidig.einde <= DAY_MS) {
      huidig.orders.push(o);
      const e = orderEnd(o);
      if (!e || !huidig.einde || e > huidig.einde) huidig.einde = e;
    } else {
      ketens.push({ orders: [o], start: s, einde: orderEnd(o) });
    }
  }
  return ketens;
}

// Startdatum van de KETEN waar elk order in zit, per order (Map op het order-object).
// Nodig voor Portfolio-evolutie: het portfolio is ontdubbeld tot het laatste order
// per EAN, en dat laatste order is bij een verlenging of productwissel niet de
// eerste keer dat de EAN in de portefeuille kwam. De lopende stand moet de EAN
// tellen vanaf het begin van de keten, niet vanaf het jongste order.
export function chainStartsByOrder(records) {
  const byGSRN = new Map();
  for (const r of records || []) {
    const g = r.connectionPointGSRN;
    if (!g) continue;
    const a = byGSRN.get(g) || [];
    a.push(r);
    byGSRN.set(g, a);
  }
  const out = new Map();
  for (const list of byGSRN.values()) {
    for (const keten of chainOrders(list)) for (const o of keten.orders) out.set(o, keten.start);
  }
  return out;
}

// Is deze aansluiting ooit echt in levering gegaan? Een actieve CP-status is het
// bewijs; anders moet er een effectieve startdatum zijn. Zelfde regel als bij de
// cohorten (cohortContracts): 2.982 ext-orders hebben géén enkele effectieve
// datum en drafts hebben er nooit een — die zijn nooit portefeuille geweest.
export const isDelivered = (rec) =>
  ACTIVE_CP_STATUSES.has(normLower(rec?.connectionPointStatus)) || !!parseDate(rec?.effectiveStartDate);

// Contractduur (in maanden) van klanten die de portefeuille écht verlaten hebben.
// Voorwaarden per keten: het laatste order staat op een vertrokken CP-status
// (zie DEPARTED_CP_STATUSES) én er is een effectieve einddatum die al gepasseerd
// is. Een keten die nog loopt valt af, ook als er tussendoor een wissel zat.
export function closedContractDurations(records, now = new Date()) {
  const byGSRN = new Map();
  for (const r of records || []) {
    const g = r.connectionPointGSRN;
    if (!g) continue;
    const a = byGSRN.get(g) || [];
    a.push(r);
    byGSRN.set(g, a);
  }
  const out = [];
  for (const [gsrn, list] of byGSRN) {
    for (const keten of chainOrders(list)) {
      const laatste = keten.orders[keten.orders.length - 1];
      if (!DEPARTED_CP_STATUSES.has(normLower(laatste.connectionPointStatus))) continue;
      const eind = parseDate(laatste.effectiveEndDate);
      if (!eind || eind > now) continue;          // loopt nog → niet meetellen
      if (!keten.start || eind <= keten.start) continue;
      out.push({
        gsrn,
        months: monthsBetween(keten.start, eind),
        start: keten.start,
        end: eind,
        orderCount: keten.orders.length,
        customer: laatste.customer || '',
        company: laatste.company || '',
        lastOrder: laatste,
      });
    }
  }
  return out.sort((a, b) => a.months - b.months);
}

// Startmaand van een contract voor de cohort-analyse: de effectieve startdatum
// als die er is, anders de geplande leveringsstart. (Het ketenen zelf blijft op
// de leveringsstart werken, want die is op élk order gevuld.)
const cohortStart = o => parseDate(o.effectiveStartDate) || parseDate(o.deliveryStartDate);

// Gemeenschappelijke basis voor de cohortanalyses: één rij per contract (keten van
// orders), met de startmaand, de huidige toestand en de bereikte looptijd.
//
// Een contract doet mee als het écht in levering is gegaan:
//   - nog actief (CP-status owned) → de status zelf is het bewijs;
//   - vertrokken (CP-status ext) → alleen mét een effectieve startdatum.
// Dat tweede is essentieel: 2.982 ext-orders hebben géén enkele effectieve datum
// en 2.504 daarvan staan nog op workflow "Start Contract" — verloren vóór de
// levering begon. Ze meetellen liet de retentie al in maand 1 instorten.
//
// Contracten met een startdatum in de toekomst vallen eveneens af: die staan op
// `ext` omdat de levering nog moet beginnen, niet omdat de klant weg is.
function cohortContracts(records, now = new Date()) {
  const byGSRN = new Map();
  for (const r of records || []) {
    const g = r.connectionPointGSRN;
    if (!g) continue;
    const a = byGSRN.get(g) || [];
    a.push(r);
    byGSRN.set(g, a);
  }
  const out = [];
  for (const [gsrn, list] of byGSRN) {
    for (const keten of chainOrders(list)) {
      const eerste = keten.orders[0];
      const laatste = keten.orders[keten.orders.length - 1];
      const status = normLower(laatste.connectionPointStatus);
      const actief = status === 'owned';
      const vertrokken = DEPARTED_CP_STATUSES.has(status);
      if (!actief && !vertrokken) continue;
      const geleverd = actief || keten.orders.some(o => parseDate(o.effectiveStartDate));
      if (!geleverd) continue;
      const start = cohortStart(eerste) || keten.start;
      if (!start || start > now) continue;
      const maand = ymKey(start);
      if (!maand) continue;
      // Einde van een vertrokken contract: effectief einde, anders de loss-datum,
      // anders het geplande einde. Nooit later dan vandaag — het is al weg.
      let eind = null;
      if (vertrokken) {
        eind = parseDate(laatste.effectiveEndDate) || parseDate(laatste.lossDate) || parseDate(laatste.deliveryEndDate);
        if (eind && eind > now) eind = now;
      }
      out.push({
        gsrn,
        maand,
        // Energielabel van de keten — nodig om een cohort per label te kunnen
        // afkappen (zie minMaandPerLabel in cohortRetentionMatrix).
        company: eerste.company || laatste.company || '',
        start,
        actief,
        vertrokken,
        eind,
        duur: vertrokken ? (eind && eind > start ? monthsBetween(start, eind) : 0) : null,
        leeftijd: monthsBetween(start, now),
      });
    }
  }
  return out;
}

// Cohortanalyse per startmaand: van alle contracten die in maand M in levering
// gingen, hoeveel zijn er nu nog actief (CP-status owned) en hoeveel zijn er
// intussen vertrokken (CP-status ext)? Eén keten van orders — dus inclusief
// verlengingen, productwissels en correcties — telt als één contract, geboekt op
// de maand waarin de levering begon. De eindstatus komt van het laatste order in
// de keten. Aansluitingen met een andere status (in, int_in, int_out) horen in
// geen van beide categorieën en blijven buiten de grafiek.
//
// Contracten met een startdatum in de TOEKOMST vallen af. Die staan op `ext`
// omdat de levering nog niet begonnen is, niet omdat de klant vertrokken is; ze
// meetellen leverde toekomstige maanden op met 100% "vertrokken".
export function contractsByStartMonth(records, now = new Date()) {
  const perMaand = new Map();
  for (const c of cohortContracts(records, now)) {
    if (!perMaand.has(c.maand)) perMaand.set(c.maand, { month: c.maand, actief: 0, vertrokken: 0 });
    perMaand.get(c.maand)[c.actief ? 'actief' : 'vertrokken']++;
  }
  return [...perMaand.values()]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map(m => {
      const totaal = m.actief + m.vertrokken;
      return {
        ...m,
        totaal,
        actiefPct: totaal > 0 ? (m.actief / totaal) * 100 : 0,
        vertrokkenPct: totaal > 0 ? (m.vertrokken / totaal) * 100 : 0,
      };
    });
}

// Vertreksoorten die betekenen dat een aansluiting de portefeuille verlaat of
// verlaten heeft. Een order dat hier NIET onder valt telt als een aansluiting die
// we nog hebben.
const VERTREKKENDE_SOORTEN = new Set([DEPARTURE_DROP, DEPARTURE_EOC, DEPARTURE_LOSS]);

// "Multi EAN klant": hoeveel ANDERE EAN's heeft deze klant, naast de aansluiting
// in de rij, die niet in drop, EOC of loss zitten — dus die we behouden? Een
// aantal, geen ja/nee: "2" zegt meteen hoeveel er van deze klant overblijft.
//
// Dezelfde klant wordt herkend op klant-id (customerId) én op e-mailadres — één
// van beide volstaat, want de bron levert ze niet altijd allebei. Vergelijking op
// GSRN-niveau: andere orders op dezelfde aansluiting tellen niet mee, anders zou
// elke verlenging al een "andere aansluiting" opleveren.
//
// Levert een functie terug die per record het aantal blijvende EAN's geeft (0 als
// er geen zijn); de index wordt één keer opgebouwd zodat het niet per rij opnieuw
// hoeft.
export function multiConnectionLookup(records) {
  const perSleutel = new Map();   // sleutel → Set van GSRN's die we nog hebben
  const sleutelsVan = (r) => {
    const s = [];
    const id = normLower(r.customerId);
    if (id) s.push(`id:${id}`);
    const mail = normLower(r.customerEmail);
    if (mail) s.push(`mail:${mail}`);
    return s;
  };
  for (const r of records || []) {
    const gsrn = (r.connectionPointGSRN || '').trim();
    if (!gsrn) continue;
    if (VERTREKKENDE_SOORTEN.has(classifyDeparture(r))) continue;
    for (const sleutel of sleutelsVan(r)) {
      if (!perSleutel.has(sleutel)) perSleutel.set(sleutel, new Set());
      perSleutel.get(sleutel).add(gsrn);
    }
  }
  return (rec) => {
    if (!rec) return 0;
    const eigen = (rec.connectionPointGSRN || '').trim();
    // Klant-id en e-mail kunnen elk een (deels) andere verzameling opleveren;
    // samen nemen en de eigen EAN eruit, zodat één klant nooit dubbel telt.
    const andere = new Set();
    for (const sleutel of sleutelsVan(rec)) {
      for (const gsrn of perSleutel.get(sleutel) || []) if (gsrn !== eigen) andere.add(gsrn);
    }
    return andere.size;
  };
}

// Cohortmatrix: per startmaand (rij) hoeveel procent van de contracten er na
// N maanden (kolom) nog liep. Zo zie je meteen of recentere lichtingen beter of
// slechter blijven dan oudere.
//
// Een contract "haalt" maand N als het op dat moment nog geleverd werd:
//   - nog actief (CP-status owned) → haalt elke maand
//   - vertrokken → haalt N zolang de contractduur ≥ N is (volledige maanden,
//     dezelfde telling als de contractduur-grafiek)
//
// Cellen die nog niet gemeten kúnnen zijn — een lichting van 3 maanden oud heeft
// nog geen maand 6 — blijven leeg (null) in plaats van 0%. Anders zou elke jonge
// lichting kunstmatig slecht scoren.
//
// `opties.minMaandPerLabel` kapt de reeks per energielabel af: `{ 'X': '2025-01' }`
// laat de lichtingen van label X vóór januari 2025 volledig weg. Bedoeld voor
// labels waarvan de oudere historiek overgenomen data is die de retentie
// vertekent. De afkapping gebeurt op het CONTRACT (de keten), niet op de rijen
// achteraf — zo blijven het gemiddelde, de kolombreedte en de totalen kloppen.
export function cohortRetentionMatrix(records, now = new Date(), opties = {}) {
  const minPerLabel = opties.minMaandPerLabel || {};
  const contracten = cohortContracts(records, now).filter(c => {
    const min = minPerLabel[c.company];
    return !min || c.maand >= min;
  });
  if (contracten.length === 0) return { offsets: [], rows: [], gemiddeld: [] };

  const maanden = [...new Set(contracten.map(c => c.maand))].sort();
  const maxOffset = contracten.reduce((m, c) => Math.max(m, c.leeftijd), 0);
  const offsets = Array.from({ length: maxOffset + 1 }, (_, i) => i);

  // Haalt dit contract maand `offset`? Nog-actieve contracten halen alles;
  // vertrokken contracten tot en met hun bereikte duur.
  const haalt = (c, offset) => c.actief || (c.duur != null && c.duur >= offset);

  const rows = maanden.map(maand => {
    const groep = contracten.filter(c => c.maand === maand);
    const leeftijd = groep.reduce((m, c) => Math.max(m, c.leeftijd), 0);
    return {
      month: maand,
      size: groep.length,
      actief: groep.filter(c => c.actief).length,
      vertrokken: groep.filter(c => c.vertrokken).length,
      medianeDuur: (() => {
        const d = groep.filter(c => c.vertrokken).map(c => c.duur).sort((a, b) => a - b);
        return d.length ? d[Math.floor(d.length / 2)] : null;
      })(),
      maxOffset: leeftijd,
      cells: offsets.map(offset => {
        if (offset > leeftijd) return null;                   // nog niet meetbaar
        const n = groep.filter(c => haalt(c, offset)).length;
        return { offset, retained: n, pct: (n / groep.length) * 100 };
      }),
    };
  });

  // Gewogen gemiddelde over alle lichtingen die de betreffende maand al bereikt
  // hebben — de onderste rij van de matrix.
  const gemiddeld = offsets.map(offset => {
    const groep = contracten.filter(c => c.leeftijd >= offset);
    if (groep.length === 0) return null;
    const n = groep.filter(c => haalt(c, offset)).length;
    return { offset, retained: n, size: groep.length, pct: (n / groep.length) * 100 };
  });

  return { offsets, rows, gemiddeld };
}

// Histogram van de contractduren, gebonden in vakjes van `binSize` maanden
// (1, 3 of 6). Levert een aaneengesloten reeks vanaf 0 tot en met de langste
// duur, zodat lege maanden als gat zichtbaar blijven in plaats van weggelaten.
export function binDurations(durations, binSize = 1) {
  const stap = Math.max(1, Math.round(binSize));
  if (!durations || durations.length === 0) return [];
  const max = durations.reduce((m, d) => Math.max(m, d.months), 0);
  const bins = [];
  for (let van = 0; van <= max; van += stap) {
    const tot = van + stap - 1;
    bins.push({
      van,
      tot,
      name: stap === 1 ? String(van) : `${van}–${tot}`,
      count: 0,
    });
  }
  for (const d of durations) bins[Math.floor(d.months / stap)].count++;
  return bins;
}

// Datakwaliteits-issues: array van {issue, rec} voor elke probleem-rij
export function detectDataQualityIssues(records) {
  const out = [];
  const gsrnCounts = new Map();
  for (const r of records || []) {
    if (r.connectionPointGSRN) gsrnCounts.set(r.connectionPointGSRN, (gsrnCounts.get(r.connectionPointGSRN) || 0) + 1);

    const hasLoss = !!parseDate(r.lossDate);
    const hasStop = !!(r.stopContractType || r.stopContractTypeDescription);
    if (hasLoss && !hasStop) out.push({ issue: 'loss_zonder_stop', rec: r });
    if (hasStop && !hasLoss) out.push({ issue: 'stop_zonder_loss', rec: r });
    if (!r.deliveryStartDate && !r.effectiveStartDate) out.push({ issue: 'geen_startdatum', rec: r });
    if (!r.connectionPointGSRN) out.push({ issue: 'geen_gsrn', rec: r });
    const start = parseDate(r.deliveryStartDate) || parseDate(r.effectiveStartDate);
    const effEnd = parseDate(r.effectiveEndDate);
    if (start && effEnd && effEnd < start) out.push({ issue: 'eind_voor_start', rec: r });
  }
  for (const [gsrn, n] of gsrnCounts) {
    if (n > 1) {
      // Toon de hoogste 'cur' record voor drill-down
      out.push({ issue: 'duplicaat_gsrn', rec: { connectionPointGSRN: gsrn, _count: n } });
    }
  }
  return out;
}

// Is het bij deze twee opeenvolgende orders op dezelfde GSRN een ANDERE klant?
//
// De KLANTNAAM is het beslissende signaal; de identificatoren dienen als
// tegenbewijs. Dat is met opzet zo, want een verschillende `customerId` betekent
// níét automatisch een andere klant: gemeten op 20.822 orders had 82 van de 118
// paren met een afwijkende id exact dezelfde naam — dubbele klantfiches in het
// bronsysteem, geen nieuwe bewoner. Omgekeerd is een naamsverschil bij een
// bevestigd gelijke id (huwelijksnaam, correctie) ook geen klantenwissel.
//
// Volgorde:
//   1. Zelfde `customerId` (zelfde bronsysteem) → dezelfde klant.
//   2. Zelfde e-mailadres → dezelfde klant (vangt een dubbele fiche met een
//      licht afwijkende naamnotatie).
//   3. Beide namen gevuld → verschillend = andere klant.
//   4. Niets te vergelijken → dezelfde klant; liever een klantenwissel missen dan
//      er een verzinnen.
//
// `customerId` is de stabiele sleutel die de backend afleidt uit de relatie-id
// van de bronsystemen ("odoo:950" / "lynx:153"). Gebruik NIET
// `customerReference`: dat is een ORDER-referentie (bv. "P2Y/2024/05/4") en
// verschilt dus per order van dezelfde klant.
function isDifferentCustomer(oldO, newO) {
  const norm = s => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const idA = norm(oldO.customerId);
  const idB = norm(newO.customerId);
  const bron = s => s.split(':')[0];
  if (idA && idB && bron(idA) === bron(idB) && idA === idB) return false;
  const mailA = norm(oldO.customerEmail);
  const mailB = norm(newO.customerEmail);
  if (mailA && mailA === mailB) return false;
  const nameA = norm(oldO.customer);
  const nameB = norm(newO.customer);
  if (nameA && nameB) return nameA !== nameB;
  return false;
}

// Begint het nieuwe order op exact dezelfde dag als het oude? Dan is er geen
// periode waarin het oude product effectief liep: het nieuwe order VERVANGT het
// oude vanaf dag één. Dat is een correctie op het contract (verkeerd product of
// verkeerde klant ingegeven), geen wissel tijdens de looptijd. Vergelijking op
// YYYY-MM-DD zodat een tijdcomponent of tijdzone geen roet in het eten gooit.
function isSameStartDay(oldO, newO) {
  const a = String(oldO.deliveryStartDate || '').substring(0, 10);
  const b = String(newO.deliveryStartDate || '').substring(0, 10);
  return !!a && a === b;
}

// Detecteer productwissels, klantenwissels, correcties en contractverlengingen.
//
// Alle patronen vereisen meerdere orders voor één GSRN met een actieve
// CP-status (owned/in/int_in). Per opeenvolgend paar (oud → nieuw):
//
//   - Productwissel:    nieuw heeft andere productGroep ÉN
//                       nieuw.deliveryStartDate < oud.deliveryEndDate (overlap)
//   - Klantenwissel:      hetzelfde patroon, maar de KLANT verschilt ook. Dan is het
//                       geen klant die van product wisselt maar een nieuwe bewoner/
//                       eigenaar op dezelfde aansluiting; die hoort niet thuis in de
//                       retentiecijfers van productwissels en gaat naar een eigen lijst.
//   - Correctie:        dezelfde voorwaarden, maar oud en nieuw order STARTEN op
//                       dezelfde dag. Het oude order heeft dan nooit gelopen; het
//                       gaat om een rechtzetting, niet om een beweging in het
//                       portfolio. Uitgesplitst naar enkel het product
//                       (productCorrections) of ook de klant (customerProductCorrections).
//   - Contractverlenging: nieuw.effectiveStartDate === oud.deliveryEndDate (aansluitend)
//
// Switch heeft voorrang als beide condities matchen (specifieker — overlap +
// product-change).
export function detectProductSwitchesAndRenewals(records) {
  const byGSRN = new Map();
  for (const r of records || []) {
    if (!ACTIVE_CP_STATUSES.has((r.connectionPointStatus || '').toLowerCase())) continue;
    if (!r.connectionPointGSRN) continue;
    const arr = byGSRN.get(r.connectionPointGSRN) || [];
    arr.push(r);
    byGSRN.set(r.connectionPointGSRN, arr);
  }
  const switches = [];
  const customerSwitches = [];
  const productCorrections = [];
  const customerProductCorrections = [];
  const renewals = [];
  const ts = r => r.orderDateTime || r.orderDate || '';
  for (const [gsrn, list] of byGSRN) {
    if (list.length < 2) continue;
    list.sort((a, b) => ts(a).localeCompare(ts(b)));
    for (let i = 1; i < list.length; i++) {
      const oldO = list[i - 1];
      const newO = list[i];
      const oldEnd = parseDate(oldO.deliveryEndDate);
      const newStart = parseDate(newO.deliveryStartDate);
      const differentProduct = (newO.productGroep || '') !== (oldO.productGroep || '');
      const isSwitch = differentProduct && oldEnd && newStart && newStart < oldEnd;
      // Vergelijk op YYYY-MM-DD niveau om timezone-issues te vermijden
      const oldEndStr = (oldO.deliveryEndDate || '').substring(0, 10);
      const newEffStartStr = (newO.effectiveStartDate || '').substring(0, 10);
      const isRenewal = oldEndStr && newEffStartStr && oldEndStr === newEffStartStr;
      if (isSwitch) {
        // Twee assen: wisselde ook de klant, en startte het nieuwe order op
        // dezelfde dag (→ correctie i.p.v. wissel)?
        const andereKlant = isDifferentCustomer(oldO, newO);
        const correctie = isSameStartDay(oldO, newO);
        const doel = correctie
          ? (andereKlant ? customerProductCorrections : productCorrections)
          : (andereKlant ? customerSwitches : switches);
        doel.push({ gsrn, old: oldO, new: newO });
      }
      else if (isRenewal) renewals.push({ gsrn, old: oldO, new: newO });
    }
  }
  return { switches, customerSwitches, productCorrections, customerProductCorrections, renewals };
}

// Tellingen per maand voor switches en renewals — voor de bar chart.
// Gebruikt newOrder.orderDate als event-moment.
export function aggregateByMonth(events) {
  const map = {};
  for (const e of events || []) {
    const d = e.new?.orderDate || e.new?.orderDateTime || '';
    const m = d.substring(0, 7);
    if (!m) continue;
    map[m] = (map[m] || 0) + 1;
  }
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).map(([month, count]) => ({ month, count }));
}

// Hoofdaggregator. Levert KPI's + maandseries + verdelingen.
// `records` is de scope-gefilterde, ruwe order-array (NIET deduped).
// Maandreeks voor de grafiek "Portfolio-evolutie": per maand wat erbij kwam
// (op leveringsstart) en wat vertrok (op exit-datum, gesplitst per vertreksoort),
// plus de lopende stand `active_end` en het saldo `netto`.
//
// `waarde(rec)` bepaalt WAT er geteld wordt. Standaard 1 per aansluiting; geef
// bijvoorbeeld `r => r.estOfftakeKwh / 1000` mee om in MWh te werken in plaats
// van in aantallen. De reeksnamen blijven identiek, zodat de grafiek er niets
// van hoeft te weten.
//
// Verwacht de VERRIJKTE portfolio-records uit computePortfolioKPIs (_status,
// _exit, _departureKind, _isExcludedReason, _isEarly).
//
// `verlengingIds` (optioneel) is een Set van order-id's die een CONTRACT-
// VERLENGING zijn: hetzelfde aansluitpunt, een nieuw order dat begint op de dag
// dat het vorige eindigde (zie detectProductSwitchesAndRenewals). Die staan als
// `new_verlenging` in de staaf van hun startmaand — er start immers een contract —
// maar het is geen aanwas: de aansluiting zat al in de portefeuille.
//
// WANNEER EEN EAN ERBIJ KOMT: op de start van zijn KETEN (`_chainStart`, gezet in
// computePortfolioKPIs), niet op de start van het jongste order. Het portfolio is
// ontdubbeld tot dat jongste order, en bij een verlenging of productwissel is dat
// order niet het moment waarop de EAN actief werd. Zonder die correctie telde
// elke verlenging als een nieuwe aansluiting op de verlengingsdatum en steeg de
// lijn "Totaal actief" met 1.214 EAN's die al actief waren (gemeten 10-09-2026).
// `new_nieuw` staat dus in de ketenstartmaand; een verlenging zónder bekende
// ketenstart telt nergens als nieuw (de oorsprong is onbekend).
//
// De lopende stand en het saldo rekenen met `new_nieuw − lost`. `new` blijft de
// som `new_nieuw + new_verlenging` per maand (de hoogte van de gestapelde staaf).
//
// ALLEEN GELEVERDE AANSLUITINGEN (`_delivered`, zie computePortfolioKPIs). Een
// draft-order (pijplijn) of een ext-order zonder effectieve startdatum (verloren
// vóór de levering begon) is nooit in de portefeuille geweest. Ze meetellen liet
// de lijn eindigen op 10.641 waar het kengetal Actief 4.784 zei (gemeten
// 10-09-2026: 3.377 drafts + 2.449 ext zonder effectieve start). Records zonder
// het veld (oudere aanroepen, tests) tellen gewoon mee.
export function buildMonthlySeries(portfolio, waarde = () => 1, verlengingIds = null) {
  const monthly = {};
  const touchMonth = m => { if (!monthly[m]) monthly[m] = { month: m, new: 0, new_nieuw: 0, new_verlenging: 0, lost: 0, lost_eoc: 0, lost_drop: 0, lost_loss: 0, lost_ilc: 0, lost_other: 0, lost_internal: 0, early: 0, active_end: 0 }; };
  const isVerlenging = (r) => !!(verlengingIds && r.id != null && verlengingIds.has(r.id));
  for (const r of portfolio || []) {
    if (r._delivered === false) continue;
    const v = Number(waarde(r)) || 0;
    const start = parseDate(r.deliveryStartDate) || parseDate(r.effectiveStartDate);
    const verlenging = isVerlenging(r);
    const ketenStart = r._chainStart || (verlenging ? null : start);
    if (ketenStart) {
      const m = ymKey(ketenStart);
      if (m) { touchMonth(m); monthly[m].new_nieuw += v; monthly[m].new += v; }
    }
    if (verlenging && start) {
      const m = ymKey(start);
      if (m) { touchMonth(m); monthly[m].new_verlenging += v; monthly[m].new += v; }
    }
    if (LOST_STATUSES.has(r._status) && r._exit) {
      const m = ymKey(r._exit);
      if (m) {
        touchMonth(m);
        monthly[m].lost += v;
        // Split per vertreksoort. Records zonder vertrek-workflow (Start/Manage
        // Contract, Rectificatie) verschijnen in `lost` maar in geen enkele reeks.
        if (!r._isExcludedReason) {
          if (r._departureKind === DEPARTURE_EOC) monthly[m].lost_eoc += v;
          else if (r._departureKind === DEPARTURE_DROP) monthly[m].lost_drop += v;
          else if (r._departureKind === DEPARTURE_LOSS) monthly[m].lost_loss += v;
          else if (r._departureKind === DEPARTURE_ILC) monthly[m].lost_ilc += v;
          else if (r._departureKind === DEPARTURE_INTERNAL) monthly[m].lost_internal += v;
          else monthly[m].lost_other += v;   // mystery switch + onbekend
        }
        if (r._isEarly) monthly[m].early += v;
      }
    }
  }
  const sortedMonths = Object.keys(monthly).sort();
  let running = 0;
  for (const m of sortedMonths) {
    running += monthly[m].new_nieuw - monthly[m].lost;
    monthly[m].active_end = running;
    monthly[m].netto = monthly[m].new_nieuw - monthly[m].lost;
  }
  return sortedMonths.map(m => monthly[m]);
}

// `period`: { from?: Date|null, to?: Date|null } — als from null = alles
export function computePortfolioKPIs(records, period = {}, now = new Date()) {
  const from = period.from || null;
  const to = period.to || now;
  const upcomingCut = addDays(now, UPCOMING_WINDOW_DAYS);

  // Start van de keten (verlengingen/productwissels op dezelfde EAN aaneen) per
  // order; zie chainStartsByOrder en buildMonthlySeries.
  const ketenStarts = chainStartsByOrder(records);
  // Dedupe-set vormt het portfolio (uniek per GSRN)
  const portfolio = dedupeByGSRNLatest(records).map(rec => {
    const stand = vertrekStand(rec, now);
    const status = STAND_NAAR_STATUS[stand.stand] || 'unknown';
    return {
      ...rec,
      _chainStart: ketenStarts.get(rec) || null,
      _delivered: isDelivered(rec),
      _status: status,
      _stand: stand.stand,
      _uitkomst: stand.uitkomst,
      _historiek: stand.historiek,               // { soort, datum, uitkomst, label } | null
      _teruggewonnen: !!stand.teruggewonnen,
      _planned: stand.stand === STAND_GEPLAND_VERTREK,   // vertrek aangekondigd, > 90 dagen weg
      _isEarly: isEarlyLeaver(rec),
      _exit: stand.datum ? parseDate(stand.datum) : null,
      _lossReason: humanizeLossReason(rec),
      _departureKind: stand.soort || DEPARTURE_NONE,  // drop | eoc | loss | ilc | mystery_switch | internal | none | unknown
      _departureSegment: departureSegment(rec),
      // Records zonder vertrek tellen NIET mee in breakdowns + EOC/Drop counts.
      // Wel nog steeds in lostCount/churnRate — die zijn over status, niet over reden.
      _isExcludedReason: (stand.soort || DEPARTURE_NONE) === DEPARTURE_NONE,
      _durationMonths: activeDurationMonths(rec, now),
    };
  });

  const isActive = r => r._status === 'active';
  // "Vertrokken" telt óók het vertrek in afhandeling mee: de datum is voorbij en
  // enkel de marktbevestiging ontbreekt nog. Zo blijven kengetal, grafieklijn en
  // churn consistent; het aantal in afhandeling staat er apart bij.
  const isLost = r => LOST_STATUSES.has(r._status);
  const isLeaving = r => r._status === 'leaving';
  const isWillLeave = r => r._status === 'will_leave';
  const isInternalOut = r => r._status === 'internal_out';
  const isNeverStarted = r => r._status === 'never_started';
  const isReview = r => r._status === 'review';
  const isCancelled = r => isActive(r) && r._historiek && r._historiek.uitkomst === UITKOMST_GEANNULEERD;
  const isWinback = r => isActive(r) && r._teruggewonnen;

  const inPeriod = (d) => {
    if (!d) return false;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  };

  // "Nieuw" = EAN's die in de periode in de portefeuille kwamen: op de ketenstart,
  // net als de grafiek. Een verlenging in de periode is dus geen nieuwe.
  const newInPeriod = portfolio.filter(r => r._delivered && inPeriod(r._chainStart || parseDate(r.deliveryStartDate) || parseDate(r.effectiveStartDate)));
  const lostInPeriod = portfolio.filter(r => isLost(r) && inPeriod(r._exit));

  const activeCount = portfolio.filter(isActive).length;
  const lostCount = portfolio.filter(isLost).length;
  const willLeaveCount = portfolio.filter(isWillLeave).length;
  // De nieuwe standen (vertrekmodel 10-09-2026).
  const leavingCount = portfolio.filter(isLeaving).length;          // datum voorbij, markt bevestigde nog niet (zit in lostCount)
  const plannedCount = portfolio.filter(r => r._planned).length;    // vertrek aangekondigd, > 90 dagen weg (zit in activeCount)
  const internalOutCount = portfolio.filter(isInternalOut).length;  // overstap naar een ander eigen energielabel
  const neverStartedCount = portfolio.filter(isNeverStarted).length;
  const reviewCount = portfolio.filter(isReview).length;
  const cancelledCount = portfolio.filter(isCancelled).length;      // geannuleerd vertrek, klant gebleven (zit in activeCount)
  const winbackCount = portfolio.filter(isWinback).length;          // nieuw contract na een eerder vertrek (zit in activeCount)
  const earlyLeavers = portfolio.filter(r => r._isEarly);
  const earlyLeaversCount = earlyLeavers.length;
  const earlyLeaversPct = lostCount > 0 ? (earlyLeavers.filter(isLost).length / lostCount) * 100 : 0;

  // Churn rate: lostInPeriod / (active aan begin van periode = active + lostInPeriod)
  const churnDenom = activeCount + lostInPeriod.length;
  const churnRate = churnDenom > 0 ? (lostInPeriod.length / churnDenom) * 100 : 0;

  const netto = newInPeriod.length - lostInPeriod.length;

  // Maand-series: active count per maand (running balance), nieuw/vertrokken per maand.
  // Vertrokken is opgesplitst in lost_eoc (contract liep af) en lost_drop (drop / switch
  // / vroegtijdig). `lost` is gehouden als totaal voor backwards-compat.
  //
  // De aanwas is bovendien gesplitst in echt nieuwe aansluitingen en verlengingen.
  // Verlengingen worden hier bepaald, op de RUWE records: het portfolio is
  // ontdubbeld per GSRN en houdt alleen het laatste order over, dus daarin is een
  // verlenging niet meer als zodanig te herkennen — het vorige order ontbreekt.
  const verlengingIds = new Set(
    detectProductSwitchesAndRenewals(records).renewals.map(v => v.new?.id).filter(id => id != null)
  );
  const monthlySeries = buildMonthlySeries(portfolio, () => 1, verlengingIds);

  // Top vertrekredenen onder lost+will_leave records (humanized reason)
  const reasonCounts = new Map();
  // Aparte breakdown puur op stopContractType — geeft fijnere granulariteit dan
  // de humanized reason die ook flow_identifier en omschrijvingen meeneemt.
  const stopTypeCounts = new Map();
  // Telling per vertreksoort over alle lost+will_leave records.
  const kindCounts = new Map();
  for (const r of portfolio) {
    if (!isLost(r) && !isWillLeave(r) && !isInternalOut(r)) continue;
    // Workflows die geen vertrek zijn (Start/Manage Contract, Rectificatie)
    // horen in geen enkele vertrek-breakdown thuis.
    if (r._isExcludedReason) continue;
    const k = r._lossReason || 'Onbekend';
    reasonCounts.set(k, (reasonCounts.get(k) || 0) + 1);
    // Partnerrollen krijgen `stopContractType`/`flowIdentifier` niet meer mee (zie
    // ORDER_WORKFLOW_LAST_FIELDS in de backend). Zonder deze terugval op de
    // afgeleide reden zou deze taart voor hen volledig "Onbekend" worden — een
    // leeg scherm in plaats van afscherming.
    // Een onbekend vertrek (CP ext zonder vertrek-workflow) of een interne
    // overstap heeft geen scenario in de bron; die krijgen hun soortnaam, niet
    // de naam van een workflow die niets met het vertrek te maken had.
    const sct = (r._departureKind === DEPARTURE_UNKNOWN || r._departureKind === DEPARTURE_INTERNAL)
      ? DEPARTURE_KIND_LABELS[r._departureKind]
      : (r.stopContractType && (STOP_CONTRACT_TYPE_LABELS[normLower(r.stopContractType)] || r.stopContractType.trim()))
      || FLOW_IDENTIFIER_MAP[normLower(r.flowIdentifier)]
      || (r.flowIdentifier || '').trim()
      || r.vertrekReden
      || DEPARTURE_KIND_LABELS[r._departureKind]
      || DEPARTURE_KIND_LABELS[DEPARTURE_UNKNOWN];   // geen workflow → "Onbekend"
    stopTypeCounts.set(sct, (stopTypeCounts.get(sct) || 0) + 1);
    kindCounts.set(r._departureKind, (kindCounts.get(r._departureKind) || 0) + 1);
  }
  const kindCount = kind => kindCounts.get(kind) || 0;
  const eocCount = kindCount(DEPARTURE_EOC);
  const dropCount = kindCount(DEPARTURE_DROP);
  const lossCount = kindCount(DEPARTURE_LOSS);
  const ilcCount = kindCount(DEPARTURE_ILC);
  const mysterySwitchCount = kindCount(DEPARTURE_MYSTERY_SWITCH);
  const unknownDepartureCount = kindCount(DEPARTURE_UNKNOWN);
  const internalDepartureCount = kindCount(DEPARTURE_INTERNAL);
  const departureTotal = dropCount + eocCount + lossCount + ilcCount + mysterySwitchCount + internalDepartureCount + unknownDepartureCount;
  // Vaste volgorde zodat kleur en legende niet kunnen verspringen.
  const departureBreakdown = [DEPARTURE_DROP, DEPARTURE_EOC, DEPARTURE_LOSS, DEPARTURE_ILC, DEPARTURE_MYSTERY_SWITCH, DEPARTURE_INTERNAL, DEPARTURE_UNKNOWN]
    .map(kind => ({
      kind,
      name: DEPARTURE_KIND_LABELS[kind],
      count: kindCount(kind),
      pct: departureTotal > 0 ? (kindCount(kind) / departureTotal) * 100 : 0,
    }))
    .filter(d => d.count > 0);
  const reasonTotal = [...reasonCounts.values()].reduce((s, v) => s + v, 0);
  const topReasons = [...reasonCounts.entries()]
    .map(([name, count]) => ({ name, count, pct: reasonTotal > 0 ? (count / reasonTotal) * 100 : 0 }))
    .sort((a, b) => b.count - a.count);
  const stopContractBreakdown = [...stopTypeCounts.entries()]
    .map(([name, count]) => ({ name, count, pct: reasonTotal > 0 ? (count / reasonTotal) * 100 : 0 }))
    .sort((a, b) => b.count - a.count);

  // Departure-kind onder uitsluitend reeds vertrokken records (voor KPI's),
  // óók filteren op excluded reasons om consistent te zijn met de breakdowns.
  let eocLostCount = 0, dropLostCount = 0, lossLostCount = 0, ilcLostCount = 0;
  for (const r of portfolio) {
    if (!isLost(r) || r._isExcludedReason) continue;
    if (r._departureKind === DEPARTURE_EOC) eocLostCount++;
    else if (r._departureKind === DEPARTURE_DROP) dropLostCount++;
    else if (r._departureKind === DEPARTURE_LOSS) lossLostCount++;
    else if (r._departureKind === DEPARTURE_ILC) ilcLostCount++;
  }

  // De grove duurverdeling per bucket ('< 6m', '1–2j', …) is vervallen: de
  // contractduur wordt nu per maand geanalyseerd via closedContractDurations,
  // dat óók verlengingen en productwissels tot één contract samenvoegt.
  const totalDuration = portfolio.filter(r => r._durationMonths != null);
  const avgDurationMonths = totalDuration.length > 0
    ? totalDuration.reduce((s, r) => s + r._durationMonths, 0) / totalDuration.length
    : 0;

  return {
    portfolio,            // deduped + verrijkt — input voor tabellen/drill-down
    kpis: {
      activeCount,
      lostCount,
      willLeaveCount,
      newInPeriod: newInPeriod.length,
      lostInPeriodCount: lostInPeriod.length,
      netto,
      churnRate,
      earlyLeaversCount,
      earlyLeaversPct,
      avgDurationMonths,
      upcomingCut,
      // Telling per vertreksoort — totaal over lost+will_leave (vertrek-reden card)
      eocCount,
      dropCount,
      lossCount,
      ilcCount,
      mysterySwitchCount,
      unknownDepartureCount,
      departureTotal,
      // Telling per vertreksoort — uitsluitend reeds vertrokken (KPI cards)
      eocLostCount,
      dropLostCount,
      lossLostCount,
      ilcLostCount,
      // Vertrekmodel 10-09-2026
      leavingCount,
      plannedCount,
      internalOutCount,
      neverStartedCount,
      reviewCount,
      cancelledCount,
      winbackCount,
      internalDepartureCount,
    },
    monthlySeries,
    // Meegegeven zodat een andere eenheid (MWh) dezelfde splitsing nieuw/verlenging
    // kan opbouwen zonder de detectie opnieuw te draaien.
    verlengingIds,
    topReasons,
    departureBreakdown,
    stopContractBreakdown,
    upcoming: portfolio.filter(isWillLeave).sort((a, b) => (a._exit || 0) - (b._exit || 0)),
    earlyLeavers: earlyLeavers.sort((a, b) => (b._exit || 0) - (a._exit || 0)),
    lostRecords: portfolio.filter(isLost),
    newRecords: newInPeriod,
    // Vertrekmodel 10-09-2026: de nieuwe lijsten, elk gesorteerd op datum.
    leavingRecords: portfolio.filter(isLeaving).sort((a, b) => (a._exit || 0) - (b._exit || 0)),
    plannedRecords: portfolio.filter(r => r._planned).sort((a, b) => (a._exit || 0) - (b._exit || 0)),
    cancelledRecords: portfolio.filter(isCancelled).sort((a, b) => String(b._historiek?.datum || '').localeCompare(String(a._historiek?.datum || ''))),
    winbackRecords: portfolio.filter(isWinback).sort((a, b) => String(b._historiek?.datum || '').localeCompare(String(a._historiek?.datum || ''))),
    internalOutRecords: portfolio.filter(isInternalOut).sort((a, b) => (b._exit || 0) - (a._exit || 0)),
    reviewRecords: portfolio.filter(isReview),
    neverStartedRecords: portfolio.filter(isNeverStarted),
  };
}

